import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import { Close as CloseIcon } from '@opengov/react-capital-assets';
import { IStory, IStoryContent } from '@opengov/stories-shared/types';
import React from 'react';

import { MEDIA_EMBED } from '../../../stories/constants';
import { addMedia, removeMedia } from '../../../stories/utils';
import { IModalMessages, IUpdateStoryContentProps, UpdateStoryContentResponse } from '../../../types';
import { styles } from './AddEmbed.styles';

export type IAddEmbedProps = {
  isTemplate: boolean;
  storyContent: IStoryContent;
  messages: IModalMessages;
  story: IStory;
  onClose(): void;
  updateStoryContent(storyContent: IStoryContent): void;
  updateStoryContentDB(props: IUpdateStoryContentProps): Promise<UpdateStoryContentResponse>;
};

type IAddEmbedState = {
  isUpdating: boolean;
  code: string;
};

export class AddEmbed extends React.Component<IAddEmbedProps, IAddEmbedState> {
  constructor(props) {
    super(props);

    // Deconstruct prop object
    const {
      storyContent: { content, mediaIndex = 0 }
    } = props;
    // Default code
    let code = '';
    // Create a document fragment based on the module content html string
    const fragment = document.createRange().createContextualFragment(content);
    // Find the media block inside the document fragment
    const mediaBlocks = fragment.querySelectorAll('.media-block');
    const mediaBlock = mediaBlocks[mediaIndex];

    // Set code to innerHTML of mediaBlock if it exists
    if (mediaBlock && mediaBlock.innerHTML) {
      code = mediaBlock.innerHTML;
    } else {
      code = '';
    }

    this.state = {
      isUpdating: !!code,
      code
    };
  }

  handleCode = (code: string) => {
    this.setState({ code });
  };

  handleRemove = (event: React.MouseEvent) => {
    event.preventDefault();

    const {
      storyContent,
      storyContent: { mediaIndex = 0 },
      onClose,
      updateStoryContent,
      updateStoryContentDB,
      isTemplate,
      story
    } = this.props;

    onClose();
    void updateStoryContent(removeMedia(storyContent, mediaIndex));
    void updateStoryContentDB({
      storyContent: removeMedia(storyContent, mediaIndex),
      isTemplate: isTemplate,
      story: story
    });
  };

  handleSubmit = (event: React.MouseEvent) => {
    event.preventDefault();

    const { isUpdating, code } = this.state;
    const {
      storyContent,
      storyContent: { mediaIndex = 0 },
      onClose,
      updateStoryContent,
      updateStoryContentDB,
      isTemplate,
      story
    } = this.props;
    let metaStoryContent = storyContent;

    if (isUpdating) {
      metaStoryContent = removeMedia(metaStoryContent, mediaIndex);
    }

    const newStoryContent = addMedia(metaStoryContent, code, mediaIndex, MEDIA_EMBED);
    onClose();
    updateStoryContent(newStoryContent);
    void updateStoryContentDB({ storyContent: newStoryContent, isTemplate: isTemplate, story: story });
  };

  render() {
    const { isUpdating, code } = this.state;
    const { messages, onClose } = this.props;
    const titleMessage = isUpdating ? messages.updateEmbed : messages.addEmbed;
    const submitText = isUpdating ? messages.update : messages.add;

    return (
      <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="add-embed-dialog-title">
        <DialogTitle id="add-embed-dialog-title" sx={styles.dialogTitle}>
          {titleMessage}
          <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
            <SvgIcon component={CloseIcon} inheritViewBox />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <TextField
            multiline
            required
            minRows={4}
            label={messages.embedLabel}
            value={code}
            onChange={(e) => this.handleCode(e.target.value)}
            inputProps={{ 'data-test': 'embed-content', className: 'no-format' }}
            sx={styles.textField}
          />
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <Button variant="outlined" color="secondary" size="small" data-test="embed-cancel" onClick={onClose}>
            {messages.cancel}
          </Button>
          {isUpdating && (
            <Button variant="outlined" color="error" size="small" onClick={this.handleRemove}>
              {messages.remove}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={this.handleSubmit}
            disabled={!code}
            data-test="embed-save"
          >
            {submitText}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
